
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AddUserForm from './AddUserForm';
import UsersList from './UsersList';
import UserStatistics from './UserStatistics';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface UsersTabProps {
  onUserCreated?: () => void;
  isLoading?: boolean;
}

const UsersTab: React.FC<UsersTabProps> = ({ onUserCreated, isLoading = false }) => {
  const [activeTab, setActiveTab] = useState('list');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshUsers = () => {
    console.log('Refreshing user list...');
    setRefreshTrigger(prev => prev + 1);
    if (onUserCreated) {
      onUserCreated();
    }
  };

  const handleUserCreated = () => {
    console.log('User created, refreshing list and switching to list tab');
    refreshUsers();
    setActiveTab('list');
  };

  // Force refresh when the component mounts
  useEffect(() => {
    refreshUsers();
  }, []);

  const forceSync = async () => {
    try {
      const { error } = await supabase.rpc('sync_users_and_profiles');
      if (error) throw error;
      
      toast({
        title: "Synchronisation réussie",
        description: "Les profils utilisateurs ont été synchronisés avec succès."
      });
      
      refreshUsers();
    } catch (error: any) {
      console.error('Error syncing users:', error);
      toast({
        title: "Erreur de synchronisation",
        description: error.message || "Impossible de synchroniser les utilisateurs.",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Gestion des utilisateurs</CardTitle>
            <CardDescription>
              Consultez, créez, activez/désactivez et gérez tous les utilisateurs de la plateforme.
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            onClick={forceSync}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Synchroniser tous les utilisateurs
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="list" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="list">Liste des utilisateurs</TabsTrigger>
            <TabsTrigger value="create">Créer un utilisateur</TabsTrigger>
            <TabsTrigger value="stats">Statistiques</TabsTrigger>
          </TabsList>
          
          <TabsContent value="list">
            <UsersList refreshTrigger={refreshTrigger} isLoading={isLoading} />
          </TabsContent>
          
          <TabsContent value="create">
            <AddUserForm onUserCreated={handleUserCreated} />
          </TabsContent>
          
          <TabsContent value="stats">
            <UserStatistics refreshTrigger={refreshTrigger} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default UsersTab;
